import { createTheme, type ThemeOptions } from "@mui/material/styles";

/**
 * Design tokens
 * -------------
 * Palette: deep navy/ink as the structural color (not black — enterprise
 * software reads as serious, not stark), a controlled teal accent reserved
 * for primary actions and the current-status indicator, and a warm amber
 * only for warranty/attention states so it never competes with the accent.
 * Type: Manrope for headings/KPI numbers (a little more geometric and
 * confident than the body face), Inter for UI text and tables, JetBrains
 * Mono specifically for asset tags/serials/IPs — the identifiers a technician
 * actually scans and types, set apart so they're unmistakably "data".
 */
export const tokens = {
  navy: "#152238",
  navyLight: "#1F3A5F",
  ink: "#0B1220",
  teal: "#0E9488",
  tealLight: "#2DD4BF",
  amber: "#D97706",
  red: "#DC2626",
  green: "#16A34A",
  slate50: "#F6F7F9",
  slate100: "#EEF1F5",
  slate200: "#E2E6EC",
  slate400: "#94A3B8",
  slate600: "#475569",
  slate800: "#1E293B"
};

const shared: ThemeOptions = {
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"Inter", "Segoe UI", sans-serif',
    h1: { fontFamily: '"Manrope", sans-serif', fontWeight: 800 },
    h2: { fontFamily: '"Manrope", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Manrope", sans-serif', fontWeight: 700 },
    h4: { fontFamily: '"Manrope", sans-serif', fontWeight: 700 },
    h5: { fontFamily: '"Manrope", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Manrope", sans-serif', fontWeight: 600 },
    button: { fontWeight: 600, textTransform: "none" }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, boxShadow: "none" },
        contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 12, backgroundImage: "none" }
      }
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, fontSize: "0.72rem" } }
    },
    MuiTableCell: {
      styleOverrides: { head: { fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.02em", textTransform: "uppercase" } }
    }
  }
};

export const lightTheme = createTheme({
  ...shared,
  palette: {
    mode: "light",
    primary: { main: tokens.navy, light: tokens.navyLight, dark: tokens.ink },
    secondary: { main: tokens.teal, light: tokens.tealLight },
    success: { main: tokens.green },
    warning: { main: tokens.amber },
    error: { main: tokens.red },
    background: { default: tokens.slate50, paper: "#FFFFFF" },
    text: { primary: tokens.slate800, secondary: tokens.slate600 },
    divider: tokens.slate200
  }
});

export const darkTheme = createTheme({
  ...shared,
  palette: {
    mode: "dark",
    primary: { main: tokens.tealLight, light: "#5EEAD4", dark: tokens.teal },
    secondary: { main: tokens.teal },
    success: { main: "#34D399" },
    warning: { main: "#FBBF24" },
    error: { main: "#F87171" },
    background: { default: "#0B1220", paper: "#111B2E" },
    text: { primary: "#E2E8F0", secondary: "#94A3B8" },
    divider: "#1E2A3F"
  }
});

/** Monospace treatment used for asset tags, serials, MAC/IP addresses. */
export const monoFont = '"JetBrains Mono", "SFMono-Regular", monospace';
